<template>
	<div :class="{ 'has-logo': showLogo }">
		<logo v-if="showLogo" :collapse="isCollapse" />
		<div v-if="!isCollapse && showSearch" class="sidebar-search-container">
			<el-select
				v-model="search"
				:remote-method="querySearch"
				filterable
				default-first-option
				remote
				placeholder="搜索菜单"
				class="sidebar-search"
				@change="handleSearchChange"
			>
				<i slot="prefix" class="el-input__icon el-icon-search" />
				<el-option
					v-for="item in options"
					:key="item.path"
					:value="item.path"
					:label="item.title.join(' > ')"
				/>
			</el-select>
		</div>
		<el-scrollbar wrap-class="scrollbar-wrapper">
			<el-menu
				:default-active="activeMenu"
				:collapse="isCollapse"
				:background-color="variables.menuBg"
				:text-color="variables.menuText"
				unique-opened
				:active-text-color="variables.menuActiveText"
				:collapse-transition="false"
				mode="vertical"
			>
				<sidebar-item
					v-for="route in permission_routes"
					:key="route.index"
					:item="route"
					:base-path="route.path"
				/>
			</el-menu>
		</el-scrollbar>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Logo from './Logo'
import SidebarItem from './SidebarItem'
import variables from '@/styles/variables.scss'
import { userEventRemoteCollect } from '@/api/user'
import { isExternal } from '@/utils/validate'
import Fuse from 'fuse.js'
import path from 'path'

export default {
	components: { SidebarItem, Logo },
	data() {
		return {
			search: '',
			options: [],
			searchPool: [],
			fuse: undefined
		}
	},
	computed: {
		...mapGetters(['permission_routes', 'sidebar']),
		activeMenu() {
			const route = this.$route
			const { meta, path } = route
			// if set path, the sidebar will highlight the path you set
			this.handleGetUserRouterCollect()
			if (meta.activeMenu) {
				return meta.activeMenu
			}
			return path
		},
		showLogo() {
			return this.$store.state.settings.sidebarLogo
		},
		variables() {
			return variables
		},
		isCollapse() {
			return !this.sidebar.opened
		},
		showSearch() {
			const env = process.env.NODE_ENV
			return env === 'development' || env === 'dev' || env.startsWith('test')
		}
	},
	watch: {
		'$store.state.user.menu': {
			handler(value) {
				this.searchPool = this.generateRoutes(value)
			},
			immediate: true
		},
		searchPool(list) {
			this.initFuse(list)
		}
	},
	mounted() {
		if (this.$store.state.user.menu.length > 0) {
			this.searchPool = this.generateRoutes(this.$store.state.user.menu)
		}
	},
	methods: {
		handleSearchChange(val) {
			const selected = this.searchPool.find(item => item.path === val)
			if (selected) {
				if (selected.topId) {
					const menu = this.$store.state.user.menu
					const curMenu = menu.filter(item => item.meta && item.meta.id === selected.topId)
					if (curMenu.length > 0) {
						// 切换侧边栏菜单到对应的模块
						this.$store.dispatch('permission/generateRoutes', { menu: curMenu })
						// 通知 Navbar 更新顶部高亮
						this.$root.$emit('update-nav-highlight', selected.topId)
					}
				}

				if (isExternal(selected.path)) {
					window.open(selected.path, '_blank')
				} else {
					this.$router.push(selected.path)
				}
			}
			this.search = ''
			this.options = []
		},
		initFuse(list) {
			this.fuse = new Fuse(list, {
				shouldSort: true,
				threshold: 0.4,
				location: 0,
				distance: 100,
				maxPatternLength: 32,
				minMatchCharLength: 1,
				keys: [
					{
						name: 'title',
						weight: 0.7
					},
					{
						name: 'path',
						weight: 0.3
					}
				]
			})
		},
		// 遍历获取当前用户点击路由
		handleGetUserRouterCollect() {
			if (this.$route.meta.id) {
				let one_name = '',
					two_name = '',
					three_name = ''
				three_name = this.$route?.matched[1]?.meta.title
				two_name = this.$route?.matched[0]?.meta?.title
				for (let i = 0; i < this.$store.getters.nav.length; i++) {
					if (this.$route?.matched[0]?.meta?.parent_id === this.$store.getters.nav[i].id) {
						one_name = this.$store.getters.nav[i].name
						break
					}
				}
				const data = {
					type: 1,
					content: `${one_name}-${two_name}-${three_name}`,
					menu_id: this.$route.meta.id
				}
				userEventRemoteCollect(data).then(res => {})
			}
		},
		generateRoutes(routes, basePath = '/', prefixTitle = [], topId = null) {
			let res = []

			for (const router of routes) {
				// skip hidden router
				if (router.hidden) {
					continue
				}

				const currentTopId = topId || (router.meta && router.meta.id)

				const data = {
					path: path.resolve(basePath, router.path),
					title: [...prefixTitle],
					topId: currentTopId
				}

				if (router.meta && router.meta.title) {
					data.title.push(router.meta.title)

					const hasChildren =
						router.children && router.children.filter(item => !item.hidden).length > 0

					if (!hasChildren || router.alwaysShow) {
						res.push(data)
					}
				}

				// recursive child routes
				if (router.children) {
					const tempRoutes = this.generateRoutes(
						router.children,
						data.path,
						data.title,
						currentTopId
					)
					if (tempRoutes.length >= 1) {
						res = [...res, ...tempRoutes]
					}
				}
			}
			return res
		},
		querySearch(query) {
			if (query !== '') {
				this.options = this.fuse.search(query)
			} else {
				this.options = []
			}
		}
	}
}
</script>
<style lang="scss" scoped>
.sidebar-search-container {
	padding: 10px;
	background-color: #131a22;
	.sidebar-search {
		width: 100%;
		::v-deep .el-input__inner {
			background-color: #1d2631;
			border: 1px solid #2d3a4b;
			color: #fff;
			height: 32px;
			line-height: 32px;
		}
		::v-deep .el-input__icon {
			line-height: 32px;
		}
	}
}
.scrollbar-wrapper {
	padding-bottom: 10px;
}
</style>
